import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Grid, Snackbar, Container, Paper, Accordion, AccordionSummary, AccordionDetails, Stack, Avatar } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublicIcon from '@mui/icons-material/Public';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ChatIcon from '@mui/icons-material/Chat';
import { useForm } from 'react-hook-form';

function Contact() {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const onSubmit = () => {
    setOpenSnackbar(true);
    reset();
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const faqs = [
    { q: 'What is your typical lead time?', a: 'Standard lead times are 2-3 weeks for prototypes and 4-6 weeks for production runs, depending on complexity and material availability.' },
    { q: 'Do you offer expedited services?', a: 'Yes, we have a "Rapid Track" channel for urgent orders. We can deliver prototypes in as little as 3-5 days for critical projects.' },
    { q: 'What file formats do you accept?', a: 'We accept STEP (.stp), IGES (.igs), Parasolid (.x_t), and SolidWorks (.sldprt). PDF drawings are required for tolerance specifications.' },
    { q: 'What are your payment terms?', a: 'Standard terms are Net 30 for established accounts. New customers typically require a deposit. We accept wire transfers and major credit cards.' },
    { q: 'Do you provide material certifications?', a: 'Yes, full material traceability and certifications (MTC) are provided with every shipment upon request.' },
    { q: 'Can you handle international shipping?', a: 'Absolutely. We have a dedicated logistics team that handles customs clearance and international shipping to over 15 countries.' },
    { q: 'Do you offer design assistance?', a: 'Yes, our engineering team provides DFM (Design for Manufacturability) analysis to help optimize your parts for cost and production efficiency.' },
    { q: 'What is your minimum order quantity (MOQ)?', a: 'We do not have a strict MOQ. We are happy to produce single prototypes as well as high-volume production runs.' }
  ];

  return (
    <Box>
      {/* Hero Header */}
      <Box sx={{
        bgcolor: '#263238',
        color: 'white',
        py: { xs: 8, md: 16 },
        textAlign: 'center',
        backgroundImage: 'linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.6)), url(/images/factory.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}>
        <Container maxWidth="lg">
          <Typography variant="overline" color="secondary" fontWeight="bold" letterSpacing={2}>
            LET'S START A CONVERSATION
          </Typography>
          <Typography variant="h1" component="h1" fontWeight="900" sx={{ fontSize: { xs: '2.5rem', md: '4.5rem' }, mb: 2 }}>
            Get in Touch
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9, fontWeight: 300, maxWidth: '800px', mx: 'auto' }}>
            Ready to optimize your manufacturing? Our engineering team is here to help with DFM feedback and competitive quoting.
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: 12, bgcolor: '#f4f6f8' }}>
        <Container maxWidth="lg">

          {/* Contact Cards Row */}
          <Grid container spacing={4} sx={{ mb: 10 }}>
            {[
              { icon: <SupportAgentIcon fontSize="large" />, title: 'Sales & Quotes', text: 'sales@aydaindia.com', sub: '+91 9080 671 664', color: '#1a237e' },
              { icon: <GroupsIcon fontSize="large" />, title: 'Engineering Support', text: 'engineering@aydaindia.com', sub: 'DFM & Technical Qs', color: '#0d47a1' },
              { icon: <LocalShippingIcon fontSize="large" />, title: 'Logistics', text: 'logistics@aydaindia.com', sub: 'Order Tracking', color: '#1565c0' }
            ].map((contact, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    height: '100%',
                    borderRadius: 4,
                    transition: '0.3s',
                    '&:hover': { transform: 'translateY(-10px)', boxShadow: 6 }
                  }}
                >
                  <Avatar sx={{ width: 70, height: 70, bgcolor: contact.color, color: 'white', mx: 'auto', mb: 2 }}>
                    {contact.icon}
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>{contact.title}</Typography>
                  <Typography variant="body1" color="primary" sx={{ fontWeight: 'bold' }}>{contact.text}</Typography>
                  <Typography variant="body2" color="text.secondary">{contact.sub}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Detailed Information Section (NEW) */}
          <Box sx={{ mb: 12 }}>
            <Typography variant="h3" align="center" fontWeight="900" sx={{ mb: 6, color: '#263238' }}>
              Why Partner With Us?
            </Typography>
            <Grid container spacing={4}>
              {[
                { title: 'Global Reach', desc: 'Expertise in international shipping and customs clearance for seamless delivery.', icon: <PublicIcon sx={{ fontSize: 40, color: '#1e88e5' }} /> },
                { title: 'Responsive Support', desc: 'Dedicated account managers ensuring communication within 24 hours.', icon: <ChatIcon sx={{ fontSize: 40, color: '#43a047' }} /> },
                { title: 'Verified Quality', desc: 'ISO 9001:2015 certified processes guaranteeing consistent output.', icon: <VerifiedUserIcon sx={{ fontSize: 40, color: '#fb8c00' }} /> },
                { title: 'Established Facility', desc: 'State-of-the-art manufacturing unit in Coimbatore, the engineering hub of India.', icon: <BusinessIcon sx={{ fontSize: 40, color: '#8e24aa' }} /> },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Paper elevation={0} sx={{ p: 3, height: '100%', bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 2, textAlign: 'center' }}>
                    <Box sx={{ mb: 2 }}>{item.icon}</Box>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>


          <Grid container spacing={6}>
            {/* Contact Details & Map */}
            <Grid item xs={12} md={5}>
              <Stack spacing={4}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3, bgcolor: '#263238', color: 'white' }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ borderBottom: '2px solid #4fc3f7', display: 'inline-block', pb: 1, mb: 4 }}>
                    Headquarters
                  </Typography>

                  <Stack spacing={4}>
                    <Box display="flex" alignItems="flex-start">
                      <LocationOnIcon sx={{ mr: 2, fontSize: 30, color: '#4fc3f7' }} />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">AYDA INDUSTRIES PVT LTD</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.6 }}>
                          Plot No. 123, SIDCO Industrial Estate,<br />
                          Coimbatore, Tamil Nadu 641021, INDIA
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center">
                      <PhoneIcon sx={{ mr: 2, fontSize: 30, color: '#4fc3f7' }} />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">Phone</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>+91 9080 671 664</Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center">
                      <MailOutlineIcon sx={{ mr: 2, fontSize: 30, color: '#4fc3f7' }} />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">Email</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>business@aydaindia.com</Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="center">
                      <AccessTimeIcon sx={{ mr: 2, fontSize: 30, color: '#4fc3f7' }} />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">Working Hours</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>Mon - Sat: 8:00 AM - 6:00 PM</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>Sun: Closed</Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Paper>

                {/* Simulated Map Placeholder */}
                <Paper elevation={2} sx={{ height: 300, overflow: 'hidden', borderRadius: 3, position: 'relative' }}>
                  <Box
                    component="img"
                    src="https://maps.googleapis.com/maps/api/staticmap?center=Coimbatore,India&zoom=13&size=600x300&maptype=roadmap&markers=color:red%7Clabel:A%7CCoimbatore,India&key=YOUR_API_KEY" // Fallback if no real map
                    onError={(e) => { e.target.style.display = 'none'; }}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <Box sx={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexDirection: 'column', zIndex: -1
                  }}>
                    <PublicIcon sx={{ fontSize: 60, color: '#90caf9', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">Location Map</Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} href="https://maps.google.com/?q=Coimbatore,Tamil+Nadu" target="_blank">
                      View on Google Maps
                    </Button>
                  </Box>
                </Paper>
              </Stack>
            </Grid>

            {/* Inquiry Form */}
            <Grid item xs={12} md={7}>
              <Paper elevation={4} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3 }}>
                <Typography variant="overline" color="primary" fontWeight="bold">QUICK RESPONSE AWAITED</Typography>
                <Typography variant="h4" gutterBottom fontWeight="900" sx={{ mb: 1 }}>
                  Send us a Message
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Fill out the form below and our team will get back to you within 24 hours.
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        variant="outlined"
                        {...register('firstName', { required: 'First name is required' })}
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        variant="outlined"
                        {...register('lastName', { required: 'Last name is required' })}
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        variant="outlined"
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                            message: 'Invalid email address',
                          },
                        })}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        variant="outlined"
                        {...register('phone', { required: 'Phone number is required' })}
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Company Name (Optional)"
                        variant="outlined"
                        {...register('company')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="How can we help you?"
                        placeholder="Tell us about your project requirements..."
                        multiline
                        rows={5}
                        variant="outlined"
                        {...register('message', { required: 'Message is required' })}
                        error={!!errors.message}
                        helperText={errors.message?.message}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        sx={{ py: 1.8, fontSize: '1.2rem', fontWeight: 'bold', boxShadow: '0 8px 16px rgba(13, 71, 161, 0.3)' }}
                      >
                        Send Inquiry
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>
          </Grid>

          {/* FAQ Section */}
          <Box sx={{ mt: 16 }}>
            <Typography variant="h3" align="center" fontWeight="900" sx={{ mb: 8 }}>Got Questions?</Typography>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} md={8}>
                {faqs.map((faq, index) => (
                  <Accordion key={index} sx={{ mb: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, borderRadius: '8px !important', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />} sx={{ bgcolor: '#fff' }}>
                      <Typography variant="h6" fontSize="1.1rem" fontWeight="bold" color="#263238">{faq.q}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ bgcolor: '#fafafa', p: 3 }}>
                      <Typography color="text.secondary" lineHeight={1.6}>{faq.a}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>
            </Grid>
          </Box>

          <Snackbar
            open={openSnackbar}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            message="Message sent successfully! We will contact you shortly."
          />
        </Container>
      </Box>
    </Box>
  );
}

export default Contact;
